/**
 * Was der visuelle Tabellen-Editor (sheetEditor.ts) über die reine Tabelle
 * hinaus braucht – Formeln, Zellformate, Diagramm –, steht in einer Zeile
 * direkt unter der Tabelle: `[//]: #formeln=…`. Das ist eine Linkdefinition:
 * Markdown zeigt sie nicht, die Tabelle selbst enthält nur die fertigen Werte.
 * Ein Diagramm aus der Tabelle folgt als Mermaid-Block darunter, markiert mit
 * `%% aus Tabelle`, damit es beim nächsten Speichern ersetzt statt verdoppelt wird.
 *
 * Eigenes Modul, damit tableEditor.ts die Tabellen-Bibliothek nicht mitlädt.
 */

export const FORMULA_LINE = /^\[\/\/\]: #formeln=(\S+)\s*$/;
export const DIAGRAM_MARK = "%% aus Tabelle";

export type ChartType = "bar" | "line" | "barline" | "pie" | "radar" | "sankey" | "quadrant" | "treemap" | "timeline";

export interface ChartConfig {
  type: ChartType;
  title: string;
  /** true: jede Spalte ist eine Datenreihe (Beschriftung in Zeile 1); sonst jede Zeile. */
  byColumns: boolean;
}

export interface TableMeta {
  /** Zelladresse -> `=…` */
  formulas: Record<string, string>;
  /** Zelladresse -> Zellformat (siehe NUMBER_FORMATS in sheetMath.ts) */
  formats: Record<string, string>;
  chart?: ChartConfig;
}

export const emptyMeta = (): TableMeta => ({ formulas: {}, formats: {} });

export function readMeta(line: string): TableMeta {
  const m = FORMULA_LINE.exec(line.trim());
  if (!m) return emptyMeta();
  try {
    const data = JSON.parse(decodeURIComponent(m[1])) as Record<string, unknown>;
    // Erste Fassung: nur Formeln, flach als { "B2": "=…" }.
    if (!("f" in data) && !("fmt" in data) && !("chart" in data)) {
      return { formulas: data as Record<string, string>, formats: {} };
    }
    return {
      formulas: (data.f as Record<string, string>) ?? {},
      formats: (data.fmt as Record<string, string>) ?? {},
      chart: data.chart as ChartConfig | undefined,
    };
  } catch {
    return emptyMeta();
  }
}

export function metaLine(meta: TableMeta): string | null {
  const data: Record<string, unknown> = {};
  if (Object.keys(meta.formulas).length) data.f = meta.formulas;
  if (Object.keys(meta.formats).length) data.fmt = meta.formats;
  if (meta.chart) data.chart = meta.chart;
  if (!Object.keys(data).length) return null;
  // Klammern kodieren: unausgeglichene `(` in einer Formel würden die Linkdefinition sprengen.
  const enc = encodeURIComponent(JSON.stringify(data)).replace(/\(/g, "%28").replace(/\)/g, "%29");
  return `[//]: #formeln=${enc}`;
}

/**
 * Zeilenindex hinter dem Diagramm-Block, der zu einer Tabelle gehört
 * (Leerzeile, ```` ```mermaid ````, …, `%% aus Tabelle`, ```` ``` ````), oder
 * `null`, wenn ab `from` keiner steht.
 */
export function diagramBlockEnd(lines: string[], from: number): number | null {
  let i = from;
  if ((lines[i] ?? "").trim() === "") i++;
  if (!/^```mermaid\s*$/.test(lines[i] ?? "")) return null;
  for (let j = i + 1; j < lines.length; j++) {
    if (/^```\s*$/.test(lines[j])) return lines[j - 1]?.trim() === DIAGRAM_MARK ? j + 1 : null;
  }
  return null;
}
